import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft,
  Clock,
  ArrowSquareOut,
  ShareNetwork,
  BookmarkSimple,
  Buildings
} from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Country flags
const COUNTRY_FLAGS = {
  uk: "🇬🇧",
  ireland: "🇮🇪",
  usa: "🇺🇸"
};

// Exchange colors
const EXCHANGE_COLORS = {
  LSE: "bg-[#1E3A5F] text-white",
  Euronext: "bg-[#059669] text-white",
  NYSE: "bg-[#0F766E] text-white"
};

export default function StockNewsDetailPage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchArticle = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stock-news/${id}`);
      setArticle(response.data);
    } catch (err) {
      setError("Article not found");
      console.error("Error fetching article:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.headline,
        text: article.summary || article.headline,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-8">
          <div className="animate-pulse">
            <div className="h-4 bg-[#E5E7EB] rounded w-32 mb-6" />
            <div className="h-8 bg-[#E5E7EB] rounded w-3/4 mb-4" />
            <div className="h-4 bg-[#E5E7EB] rounded w-1/2 mb-8" />
            <div className="space-y-3">
              <div className="h-4 bg-[#E5E7EB] rounded" />
              <div className="h-4 bg-[#E5E7EB] rounded" />
              <div className="h-4 bg-[#E5E7EB] rounded w-2/3" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-16 text-center">
          <Buildings size={48} className="mx-auto text-[#D1D5DB] mb-4" />
          <h1 className="font-serif text-2xl text-[#374151] mb-2">Article Not Found</h1>
          <p className="text-[#6B6661] mb-6">The article you're looking for doesn't exist.</p>
          <Link
            to="/stock-news"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white hover:bg-[#064E3B] transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Stock News
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEOHead 
        title={`${article.headline} | Farmwatch Stock News`}
        description={article.summary || `${article.headline} — reported by ${article.source}. Headline and link via Farmwatch.`}
        keywords={`${article.company}, ${article.ticker}, agriculture news, stock news`}
      />
      <Header />
      
      <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-8">
        {/* Back Link */}
        <Link
          to="/stock-news"
          className="inline-flex items-center gap-2 text-[#6B6661] hover:text-[#0F766E] mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm">Back to Stock News</span>
        </Link>

        {/* Article Header */}
        <header className="mb-8">
          {/* Company Badge */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-2xl">{COUNTRY_FLAGS[article.country] || "🌍"}</span>
            <span className={`px-3 py-1 text-sm font-bold ${EXCHANGE_COLORS[article.exchange] || "bg-gray-500 text-white"}`}>
              {article.ticker}
            </span>
            <span className="text-[#374151] font-medium">{article.company}</span>
            <span className="text-xs px-2 py-0.5 bg-[#F6E9DD] text-[#6B6661] rounded">
              {article.exchange}
            </span>
          </div>

          {/* Headline */}
          <h1 className="font-serif text-3xl sm:text-4xl text-[#33302E] leading-tight mb-4">
            {article.headline}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-[#6B6661]">
            <div className="flex items-center gap-1">
              <Clock size={16} />
              {formatDate(article.published_at)}
            </div>
            <span>•</span>
            <span>Source: {article.source}</span>
          </div>
        </header>

        {/* Summary / Full Story (only for own-written stories) */}
        {article.full_story ? (
          <>
            <div className="bg-[#F0FDF4] border-l-4 border-[#0F766E] p-6 mb-8">
              <h2 className="font-semibold text-[#064E3B] mb-3 text-sm uppercase tracking-wider">Summary</h2>
              <p className="text-[#374151] leading-relaxed text-lg">
                {article.summary}
              </p>
            </div>
            <article className="prose prose-lg max-w-none mb-8">
              <div className="font-sans text-[#374151] leading-relaxed">
                {article.full_story.split('\n').map((paragraph, index) => {
                  const trimmed = paragraph.trim();
                  if (!trimmed) return <div key={index} className="h-4" />;
                  return (
                    <p key={index} className="mb-4">
                      {trimmed}
                    </p>
                  );
                })}
              </div>
            </article>
          </>
        ) : (
          <section className="bg-white border-2 border-[#0F766E] p-6 sm:p-8 mb-8" data-testid="stock-link-only-notice">
            <p className="font-mono text-[10px] uppercase tracking-wider text-[#0F766E] mb-3">Headline &amp; link only</p>
            <p className="font-serif text-xl text-[#33302E] leading-snug mb-3">
              This story is reported by <span className="font-semibold text-[#0F766E]">{article.source}</span>.
            </p>
            <p className="font-sans text-sm text-[#6B6661] mb-6">
              Farmwatch links to the original report rather than reproducing it. The full article is available at the source.
            </p>
            {article.original_url && (
              <a href={article.original_url} target="_blank" rel="noopener noreferrer" data-testid="stock-read-full-story-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#0F766E] text-white font-semibold hover:bg-[#064E3B] transition-colors">
                Read the full story at {article.source} <ArrowSquareOut size={18} weight="bold" />
              </a>
            )}
          </section>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-4 pt-6 border-t border-[#E0D4C8]">
          <button
            onClick={handleShare}
            data-testid="share-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#E0D4C8] text-[#374151] hover:border-[#0F766E] hover:text-[#0F766E] transition-colors"
          >
            <ShareNetwork size={18} />
            Share
          </button>
          
          <Link
            to={`/agri-stocks`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white hover:bg-[#064E3B] transition-colors ml-auto"
          >
            <Buildings size={18} />
            View {article.company} Profile
          </Link>
        </div>

        {/* Source Attribution */}
        <div className="mt-8 p-4 bg-[#F0FDF4] border border-[#065F46]/20">
          <p className="text-sm text-[#065F46]">
            <strong>Attribution:</strong> Headline reported by {article.source}
            {article.feed_source && article.feed_source !== article.source ? ` (via ${article.feed_source})` : ""}. Farmwatch links to the original and does not reproduce third-party text.
          </p>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-[#FEF3C7] border border-[#FDE68A] text-sm">
          <p className="text-[#92400E]">
            <strong>Disclaimer:</strong> This article is for informational purposes only and does not constitute 
            investment advice. Stock prices and company details can change. Always conduct your own research.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
